import React, {FC, ReactNode, useMemo} from 'react';
import {shallowEqual, useSelector} from 'react-redux';

import {
    MyTopPerformingGamesState,
    MyTopPerformingGamesDataItem
} from '../../reducers/fortnite-game/myTopPerformingGames.reducer';
import {MyOverallHighlightsState} from '../../reducers/fortnite-game/myOverallHighlights.reducer';
import {DataItem} from '../../reducers/fortnite-game/types';
import {RootState} from '../../index';
import {getMyOverallHighlightsState} from '../../selectors/fortnite-game/myOverallHighlightsSelector';
import {getMyTopPerformingGamesState} from '../../selectors/fortnite-game/myTopPerformingGamesSelector';

import {usePrevious} from '../../hooks/usePrevious';

export type PreviousDataItem = DataItem | MyTopPerformingGamesDataItem | {};

export interface SidebarPreviousDataContextValue {
    prevUniquePlayers: PreviousDataItem;
    prevMonthly: PreviousDataItem;
    prevWeekly: PreviousDataItem;
    prevLifetimePlays: PreviousDataItem;
    prevMostPopularGame: PreviousDataItem;
    prevHighestGrowth: PreviousDataItem;
    prevHighestAvgPlayTime: PreviousDataItem;
    prevMostVisitedGame: PreviousDataItem;
}

export interface SidebarPreviousDataProviderProps {
    children: ReactNode;
}

export const SidebarPreviousDataContext = React.createContext<SidebarPreviousDataContextValue>({
    prevUniquePlayers: {},
    prevMonthly: {},
    prevWeekly: {},
    prevLifetimePlays: {},
    prevMostPopularGame: {},
    prevHighestGrowth: {},
    prevHighestAvgPlayTime: {},
    prevMostVisitedGame: {}
});

const SidebarPreviousDataProvider: FC<SidebarPreviousDataProviderProps> = ({
    children
}: SidebarPreviousDataProviderProps) => {
    const {
        totals: {uniquePlayers, lifetimePlays},
        monthly,
        weekly
    } = useSelector<RootState, MyOverallHighlightsState>(getMyOverallHighlightsState, shallowEqual);

    const {mostPopularGame, highestGrowth, highestAvgPlayTime, mostVisitedGame} = useSelector<
        RootState,
        MyTopPerformingGamesState
    >(getMyTopPerformingGamesState, shallowEqual);

    const getPreviousOverallHighlights = (current: DataItem): DataItem =>
        current && current.amount ? current : undefined;
    const getPreviousTopPerformingGames = (
        current: MyTopPerformingGamesDataItem
    ): MyTopPerformingGamesDataItem => (current && current.value ? current : undefined);

    const prevUniquePlayers = usePrevious(getPreviousOverallHighlights(uniquePlayers));
    const prevMonthly = usePrevious(getPreviousOverallHighlights(monthly));
    const prevWeekly = usePrevious(getPreviousOverallHighlights(weekly));
    const prevLifetimePlays = usePrevious(getPreviousOverallHighlights(lifetimePlays));

    const prevMostPopularGame = usePrevious(getPreviousTopPerformingGames(mostPopularGame));
    const prevHighestGrowth = usePrevious(getPreviousTopPerformingGames(highestGrowth));
    const prevHighestAvgPlayTime = usePrevious(getPreviousTopPerformingGames(highestAvgPlayTime));
    const prevMostVisitedGame = usePrevious(getPreviousTopPerformingGames(mostVisitedGame));

    const getPrevious = (previous: PreviousDataItem): PreviousDataItem =>
        previous ? previous : {};

    const value = useMemo(
        () => ({
            prevUniquePlayers: getPrevious(prevUniquePlayers),
            prevMonthly: getPrevious(prevMonthly),
            prevWeekly: getPrevious(prevWeekly),
            prevLifetimePlays: getPrevious(prevLifetimePlays),
            prevMostPopularGame: getPrevious(prevMostPopularGame),
            prevHighestGrowth: getPrevious(prevHighestGrowth),
            prevHighestAvgPlayTime: getPrevious(prevHighestAvgPlayTime),
            prevMostVisitedGame: getPrevious(prevMostVisitedGame)
        }),
        [
            prevUniquePlayers,
            prevMonthly,
            prevWeekly,
            prevLifetimePlays,
            prevMostPopularGame,
            prevHighestGrowth,
            prevHighestAvgPlayTime,
            prevMostVisitedGame
        ]
    );

    return (
        <SidebarPreviousDataContext.Provider value={value}>
            {children}
        </SidebarPreviousDataContext.Provider>
    );
};

export default SidebarPreviousDataProvider;
